import React from 'react';
import { GenericSearchSelect, SelectableItem } from '../../../../components/library/info-order/OrderModules/components/GenericSearchSelect';
import { FacturacionTarifas } from '../../../../types/types.facturacion';

// Interfaz específica para estudios de preórdenes
export interface PreorderStudyItem extends SelectableItem {
  id: number;
  code: string;
  name: string;
  labref?: number;
  group: string;
  price?: number;
  // Campos adicionales específicos de FacturacionTarifas
  Tf_Es_Id: number;
  Tf_Es_Codigo: string;
  Tf_Es_Nombre: string;
  Tf_Aa_Nombre: string;
  Tf_ValorTarifa: number;
}

interface PreorderStudiesSearchSelectProps {
  dataSource: FacturacionTarifas[];
  onStudySelected: (study: PreorderStudyItem) => void;
  selectedStudies?: PreorderStudyItem[];
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
}

export const PreorderStudiesSearchSelect: React.FC<PreorderStudiesSearchSelectProps> = ({
  dataSource,
  onStudySelected,
  selectedStudies = [],
  placeholder = "Buscar estudios...",
  label = "Estudios",
  disabled = false,
  className = "comboboxItems"
}) => {
  
  // Crear el data source genérico a partir de las tarifas con lazy loading
  const genericDataSource = {
    load: async (options?: { skip?: number; take?: number; searchText?: string }): Promise<PreorderStudyItem[]> => {
      const { skip = 0, take = 200, searchText = '' } = options || {};
      
      // Filtrar tarifas por texto de búsqueda si se proporciona
      let filteredTarifas = dataSource;
      if (searchText && searchText.length >= 2) {
        const search = searchText.toLowerCase();
        filteredTarifas = dataSource.filter(tarifa =>
          tarifa.Estudio?.Es_Nombre?.toLowerCase().includes(search) ||
          tarifa.Estudio?.Es_Codigo?.toLowerCase().includes(search) ||
          tarifa.Estudio?.SysA_AreasAnalisis?.Aa_Nombre?.toLowerCase().includes(search)
        );
      }
      
      // Simular paginación
      const paginatedTarifas = filteredTarifas.slice(skip, skip + take);
      
      return paginatedTarifas.map(tarifa => ({
        id: tarifa.Tf_Es_Id,
        code: tarifa.Estudio?.Es_Codigo || '',
        name: tarifa.Estudio?.Es_Nombre || '',
        labref: tarifa.Estudio.Es_Lr_Id,
        group: tarifa.Estudio?.SysA_AreasAnalisis?.Aa_Nombre || 'Sin Área',
        price: tarifa.Tf_Valor,
        // Campos originales para compatibilidad
        Tf_Es_Id: tarifa.Tf_Es_Id,
        Tf_Es_Codigo: tarifa.Estudio?.Es_Codigo || '',
        Tf_Es_Nombre: tarifa.Estudio?.Es_Nombre || '',
        Tf_Aa_Nombre: tarifa.Estudio?.SysA_AreasAnalisis?.Aa_Nombre || '',
        Tf_ValorTarifa: tarifa.Tf_Valor,
      }));
    },
    
    getTotalCount: async (searchText?: string): Promise<number> => {
      if (!searchText || searchText.length < 2) {
        return dataSource.length;
      }
      
      const search = searchText.toLowerCase();
      const filteredTarifas = dataSource.filter(tarifa =>
        tarifa.Estudio?.Es_Nombre?.toLowerCase().includes(search) ||
        tarifa.Estudio?.Es_Codigo?.toLowerCase().includes(search) ||
        tarifa.Estudio?.SysA_AreasAnalisis?.Aa_Nombre?.toLowerCase().includes(search)
      );
      
      return filteredTarifas.length;
    }
  };

  const handleItemSelected = (item: SelectableItem) => {
    onStudySelected(item as PreorderStudyItem);
  };

  return (
    <GenericSearchSelect
      dataSource={genericDataSource}
      onItemSelected={handleItemSelected}
      selectedItems={selectedStudies}
      placeholder={placeholder}
      label={label}
      disabled={disabled}
      className={className}
      searchFields={['name', 'code']}
      groupField="group"
      displayFields={{ title: 'name', subtitle: 'code' }}
      showGroups={true}
      minSearchLength={2}
    />
  );
};
